// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';
import _ from 'lodash';

import { WidgetGrid, JarvisWidget } from 'wdna-components/lib';
import { initProcess, finishProcess } from 'wdna-components/lib/scheduler/actions';
import { removeTab } from 'wdna-components/lib/tabs';
import Loader from 'wdna-components/lib/loaders/local/Loader';
import UiValidate from 'wdna-components/lib/forms/validation/UiValidate';
import { messageError, messageInfo, messageBoxWarning } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';
import { hasLevel } from 'wdna-components/lib/helpers/permission-new';

import { config } from '../../../../config/config';
import { lov, LovDO } from '../../../configuration/requests';
import { company, CompanyDO } from '../../requests';

import ButtonsForm from '../../../../components/ButtonsForm';
import Form from './Form';

interface Props {
  rowId?: number;
  notify?: () => void;
}

interface State {
  loading: boolean;
  update?: string;

  isFile: boolean;
  modalShow: boolean;
  modalData?: any;
  id?: number;
  name?: string;
  typeCompanyArray: LovDO[];

  data: Partial<CompanyDO>;
}

export default class Edit extends React.Component<Props, State> {
  private pid: string;
  private mounted: boolean;
  private fileId: any;
  private fileName: any;
  private defaultValues: any;

  public constructor(props: Props) {
    super(props);

    this.pid = window.getStr();
    this.mounted = false;

    this.state = {
      loading: false,

      isFile: false,
      modalShow: false,
      modalData: {},

      typeCompanyArray: [],

      data: {},
    };

    this.defaultValues = {};
    this.fileId = window.getStr();
    this.fileName = window.getStr();
  }

  public componentDidMount(): void {
    this.mounted = true;
    this.load().catch(() => { /* pass */ });
  }

  public componentWillUnmount(): void {
    this.mounted = false;
  }

  public setLocalState = (nextState: Partial<State>, callback?: (() => void)): void => {
    if (this.mounted) {
      this.setState((prevState: State) => ({
        ...prevState,
        ...nextState,
      }), callback);
    }
  };

  public load = async (): Promise<void> => {
    const { rowId } = this.props;
    const isNew: boolean = (rowId === undefined || !!isNaN(rowId));

    this.loadingStart();
    try {
      const [typeCompanyArray] = await Promise.all([
        lov<LovDO[]>('statics', 'type_company') || []
      ]);

      let rowData: Partial<CompanyDO> = this.state.data || {};
      if (!isNew) {
        rowData = await company<Partial<CompanyDO>>('get', String(rowId)) || {};
      }
      this.setLocalState({
        data: { ...rowData, },
        typeCompanyArray,
      }, () => {
        this.defaultValues = _.cloneDeep(rowData);
      });
    } catch (error) {
      messageError('Error', getErrorMessage(error));
    }
    this.loadingFinish();
  };

  public loadingStart = (): void => {
    this.setLocalState({ loading: true });
    window.store.dispatch(initProcess(this.pid));
  };

  public loadingFinish = (): void => {
    this.setLocalState({ loading: false });
    window.store.dispatch(finishProcess(this.pid));
  };

  public onReset = (): void => {
    this.setLocalState({ data: { ...this.defaultValues } });
  };

  public handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>): void => {
    const { name, value, checked, type, dataset } = (e as React.ChangeEvent<HTMLInputElement>).target;
    const { ctype } = dataset || {};
    // Process new value from ctype
    let newValue: any = (type === 'checkbox') ? !!checked : value;
    if (ctype === 'number') newValue = (/^\d+(\.\d+){0,1}$/.test(value)) ? _.toNumber(value) : null;
    // Create new data state structure
    const levels: string[] = name.split('.');
    const nextState: Record<string, any> = { [levels[0]]: { ...this.state[levels[0]] } };
    _.set(nextState, levels, newValue);
    this.setLocalState(nextState);
  };

  public handleUploadFile = (): void => {
    const { id, data } = this.state;

    const { fileId } = this;
    const file: any[] = $(`#${fileId}`).prop('files');
    if (!file[0]) {
      if (isNaN(Number(id))) {
        messageError('Error', 'Please select a valid file');
        return;
      }

      const formData: FormData = new FormData();
      formData.append('ufile', file[0]);

      if (data?.id !== undefined && !isNaN(data.id)) {
        formData.append('id', String(data.id));
      }

      const options: any = { headers: { 'Content-Type': 'multipart/form-data' } };

      this.loadingStart();
      company('dashboard', undefined, formData, options)
        ?.then(() => {
          messageInfo('Success', `Dashboard successfully ${data?.id ? 'edited' : 'created'} ${data?.name}`, 5000);
          const { notify } = this.props;
          if (typeof notify === 'function') {
            notify();
          }
          window.store.dispatch(removeTab('CE'));
        })
        .catch((error: any) => {
          this.uploadingError(error);
        })
        .finally(() => {
          this.loadingFinish();
        });
    }
  };

  public handleDeletedFile = (): void => {
    const { data } = this.state;

    messageBoxWarning('Delete', 'Do you want to delete this register ?', () => {
      const formData: FormData = new FormData();
      formData.append('id', String(data?.id));

      const options: any = { headers: { 'Content-Type': 'multipart/form-data' } };

      this.loadingStart();
      company('dashboard', undefined, formData, options)
        ?.then(() => {
          messageInfo('Success', 'Dashboard successfully deleted', 5000);
          const { notify } = this.props;
          if (typeof notify === 'function') {
            notify();
          }
          window.store.dispatch(removeTab('CE'));
        })
        .catch((error: any) => {
          this.uploadingError(error);
        })
        .finally(() => {
          this.loadingFinish();
        });
    });
  };

  public uploadingError = (error: any): void => {
    if (error) {
      messageError('Error', getErrorMessage(error));
    } else {
      messageError('Error', 'Unknown error');
    }
  };

  public onInfo = (value: any): void => {
    this.setLocalState({
      update: window.getStr(),
      modalShow: true,
      modalData: value,
    });
    const { rowId } = this.props;
    const { protocol } = location;
    const host: string = protocol.concat('//', window.location.hostname);
    const url: string = host.concat(`/dashboard/company_${rowId || ''}.json`);
    this.chkFileOnURL(url).catch(() => { /* pass */ });
  };

  public onCloseModal = (): void => {
    this.setLocalState({
      update: window.getStr(),
      modalShow: false,
    });
  };

  public submit = (e: React.FormEvent<HTMLFormElement>): any => {
    e.preventDefault();
    const { data } = this.state;

    this.loadingStart();
    company('set', undefined, data)
      ?.then(() => {
        messageInfo('Success', `Company successfully ${data?.id ? 'edited' : 'created'} ${data?.name}`, 5000);
        const { notify } = this.props;
        if (typeof notify === 'function') {
          notify();
        }
        window.store.dispatch(removeTab('CoE'));
      })
      .catch((error: any) => {
        messageError('Error', getErrorMessage(error));
      })
      .finally(() => {
        this.loadingFinish();
      });
  };

  public changeFileName = (): void => {
    const { fileId, fileName } = this;
    const file: any[] = $(`#${fileId}`).prop('files');
    if (!file[0]) {
      return;
    }

    const { name } = file[0];
    $(`#${fileName}`).val(name);
  };

  public handleChangeFile = (e: React.ChangeEvent<HTMLInputElement>): void => {
    this.changeFileName();
    const { name, value } = e.target;
    // Create new data state structure
    const [oname, iname] = name.split('.');
    const nextState: Record<string, any> = iname ? { [oname]: { [iname]: value } } : { [oname]: value };
    // Update state with new data
    this.setLocalState(nextState);
  };

  public chkFileOnURL = async (url: string): Promise<boolean> => {
    const result: boolean = await fetch(url, { method: 'HEAD', mode: 'cors' })
      .then((resp: Response) => { return resp.ok; }, () => { return false; })
      .catch(() => { return false; });
    this.setLocalState({ isFile: result });

    return result;
  };

  public download = (): void => {
    const { rowId } = this.props;
    const { isFile } = this.state;
    const { protocol } = location;
    const host: string = protocol.concat('//', window.location.hostname);
    if (isFile) {
      const url: string = host.concat(`/dashboard/company_${rowId}.json`);
      fetch(url).then((response: Response) => {
        response.blob().then((blob: Blob) => {
          const href: string = window.URL.createObjectURL(blob);
          const a: HTMLAnchorElement = document.createElement('a');
          a.href = href;
          a.download = `company_${rowId}.json`;
          a.click();
        }).catch((error: any) => {
          messageError('Error', getErrorMessage(error));
        });
      }).catch((error: any) => {
        messageError('Error', getErrorMessage(error));
      });
    }
  };

  public showFreeboard = (): void => {
    const { rowId } = this.props;
    const source: string = `${config.fbdRootUrl}#source=/dashboard/company_${rowId}.json`;
    window.open(source, `freeboard_company_${rowId}`, 'width=750,height=550,scrollbars=YES');
  };

  public render(): JSX.Element {
    const { data, loading } = this.state;
    const { fileId, fileName } = this;

    return (
      <div id="content" className="content">
        <WidgetGrid>
          <div className="row">
            <article className="col-sm-12 col-md-12 col-lg-12">
              <JarvisWidget
                editbutton={false}
                togglebutton={false}
                colorbutton={false}
                deletebutton={false}
                custombutton={false}
                fullscreenbutton={false}
              >
                <div className="widget-body">
                  <Loader show={loading} overlay={false} />
                  <UiValidate>
                    <form className="smart-form" onSubmit={this.submit} method="post" encType="multipart/form-data">
                      <header>
                        <h2>
                          <span className="widget-icon" style={{ padding: '8px' }}>
                            <i className="fa fa-edit" />
                          </span>
                          Company {data?.id ? `Edit: ${data.id}` : 'New'}
                        </h2>
                      </header>
                      <Form
                        formValues={this.state}
                        onChange={this.handleChange}
                        onChangeFile={this.handleChangeFile}
                        onCloseModal={this.onCloseModal}
                        onHandleUploadFile={this.handleUploadFile}
                        onHandleDeletedFile={this.handleDeletedFile}
                        fileId={fileId}
                        fileName={fileName}
                        download={this.download}
                        showFreeboard={this.showFreeboard}
                      />
                      <ButtonsForm
                        loading={loading}
                        disabled={!hasLevel('role', ['ADM', 'URW'])}
                        onReset={this.onReset}
                        buttons={[
                          { label: 'Dashboard', action: this.onInfo }
                        ]}
                      />
                    </form>
                  </UiValidate>
                </div>
              </JarvisWidget>
            </article>
          </div>
        </WidgetGrid>
      </div>
    );
  }
}
